import PlanCard from "@components/PlanCard"
import PlanFooterText from "@components/PlanFooterText"
import SubscriptionForm from "@components/SubscriptionForm"
import { getOnboardingContext, isHomeFlow } from "@helpers/localStorage"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"

interface Props {
  currency: string
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  timeLeft: string
  monthlyIntroPrice: StripePrice | undefined
  monthlyPrice: StripePrice | undefined
  quarterlyIntroPrice: StripePrice | undefined
  quarterlyPrice: StripePrice | undefined
  yearlyIntroPrice: StripePrice | undefined
  yearlyPrice: StripePrice | undefined
  selectedPlan: Plan
  onPlanChange: (plan: Plan) => void
}

export default function Plans({
  currency,
  isSubmitting,
  setIsSubmitting,
  timeLeft,
  monthlyIntroPrice,
  monthlyPrice,
  quarterlyIntroPrice,
  quarterlyPrice,
  yearlyIntroPrice,
  yearlyPrice,
  selectedPlan,
  onPlanChange,
}: Props) {
  const { t } = useTranslation()
  const context = getOnboardingContext()
  const isHome = isHomeFlow()
  const plansCopyKey =
    context === "cars"
      ? "pages.checkout.plansCars"
      : context === "pets"
        ? "pages.checkout.plansPets"
        : isHome
          ? "pages.checkout.plansHome"
          : "pages.checkout.plans"

  const getSelectedPriceId = () => {
    switch (selectedPlan) {
      case "monthly":
        return monthlyIntroPrice?.id
      case "quarterly":
        return quarterlyIntroPrice?.id
      case "yearly":
        return yearlyIntroPrice?.id
      default:
        return undefined
    }
  }

  const getSelectedFullPrice = () => {
    switch (selectedPlan) {
      case "monthly":
        return monthlyPrice
      case "quarterly":
        return quarterlyPrice
      case "yearly":
        return yearlyPrice
      default:
        return undefined
    }
  }

  return (
    <div className="space-y-4">
      <PlanCard
        currency={currency}
        isSubmitting={isSubmitting}
        periodAmount={1}
        periodUnit="month"
        plan="monthly"
        setIsSubmitting={setIsSubmitting}
        stripeIntroPrice={monthlyIntroPrice}
        stripePrice={monthlyPrice}
        timeLeft={timeLeft}
        isSelected={selectedPlan === "monthly"}
        onSelect={() => onPlanChange("monthly")}
      />

      <PlanCard
        bestOffer
        currency={currency}
        isSubmitting={isSubmitting}
        periodAmount={3}
        periodUnit="month"
        plan="quarterly"
        setIsSubmitting={setIsSubmitting}
        stripeIntroPrice={quarterlyIntroPrice}
        stripePrice={quarterlyPrice}
        timeLeft={timeLeft}
        isSelected={selectedPlan === "quarterly"}
        onSelect={() => onPlanChange("quarterly")}
      />

      <PlanCard
        currency={currency}
        isSubmitting={isSubmitting}
        periodAmount={1}
        periodUnit="year"
        plan="yearly"
        setIsSubmitting={setIsSubmitting}
        stripeIntroPrice={yearlyIntroPrice}
        stripePrice={yearlyPrice}
        timeLeft={timeLeft}
        isSelected={selectedPlan === "yearly"}
        onSelect={() => onPlanChange("yearly")}
      />

      <div className="space-y-4">
        <SubscriptionForm
          buttonClassName="theme-button-primary uppercase"
          buttonTitle={t(`${plansCopyKey}.continue`)}
          currency={currency}
          fullPrice={getSelectedFullPrice()}
          isSubmitting={isSubmitting}
          plan={selectedPlan}
          priceId={getSelectedPriceId()}
          setIsSubmitting={setIsSubmitting}
        />

        <PlanFooterText plan={selectedPlan} />
      </div>
    </div>
  )
}
